import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheck, BadgeCheck, SearchCheck, XCircle, Loader2, Gift } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { submitPrequalify, validateReferral, zar } from "@/api";

const INITIAL = {
  full_name: "",
  email: "",
  mobile: "",
  employment_type: "employed",
  net_income: "",
  monthly_expenses: "",
  max_affordable_payment: "",
  ownership_deposit: "",
  has_valid_licence: "yes",
  private_use_only: "yes",
  preferred_term: 48,
  vehicle_interest: "",
};

const RESULT_STYLE = {
  pre_qualified: { icon: BadgeCheck, color: "text-emerald-400", ring: "border-emerald-400/40", testid: "result-pre-qualified" },
  likely_review: { icon: SearchCheck, color: "text-amber-400", ring: "border-amber-400/40", testid: "result-likely-review" },
  not_eligible_now: { icon: XCircle, color: "text-red-400", ring: "border-red-400/40", testid: "result-not-eligible" },
};

export default function PreQualify() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [referral, setReferral] = useState(null);
  const navigate = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => {
    const code = (params.get("ref") || "").trim().toUpperCase();
    if (!code) return;
    validateReferral(code)
      .then((res) => setReferral({ code, name: res.referrer_first_name }))
      .catch(() => toast.error("That referral code isn't valid"));
  }, [params]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await submitPrequalify({
        ...form,
        net_income: Number(form.net_income),
        monthly_expenses: Number(form.monthly_expenses),
        max_affordable_payment: Number(form.max_affordable_payment),
        ownership_deposit: Number(form.ownership_deposit || 0),
        has_valid_licence: form.has_valid_licence === "yes",
        private_use_only: form.private_use_only === "yes",
        preferred_term: Number(form.preferred_term),
        referral_code: referral?.code || "",
      });
      setResult(res);
      sessionStorage.setItem("compcar_prequal", JSON.stringify(form));
      toast.success("Pre-qualification complete");
    } catch (err) {
      toast.error("Something went wrong. Please try again or call 010 534 9926.");
    } finally {
      setLoading(false);
    }
  };

  const style = result ? RESULT_STYLE[result.status] : null;

  return (
    <div data-testid="prequalify-page" className="pt-32 pb-24 min-h-screen">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-sky-300 font-bold mb-4">Client vetting — step one</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-black text-white mb-6">
            Know where you stand in two minutes.
          </h1>
          <p className="text-base text-slate-400 leading-relaxed mb-10">
            Answer a few honest questions about your income, expenses and
            licence. You'll get a provisional result instantly — no obligation,
            no paperwork, and no impact on any credit record.
          </p>
          <ul className="space-y-5">
            {[
              "Income-led assessment — employed and self-employed welcome",
              "Weak credit history doesn't mean automatic rejection",
              "Your result is provisional; final approval follows full verification",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm text-slate-300">
                <ShieldCheck size={18} className="text-[#F57C00] mt-0.5 shrink-0" />
                {t}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.15}>
          <AnimatePresence mode="wait">
            {!result ? (
              <motion.form
                key="form"
                data-testid="prequalify-form"
                onSubmit={handleSubmit}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="bg-[#122659]/80 backdrop-blur-xl border border-sky-300/20 rounded-xl p-8 md:p-10 space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-2">
                    <label className="input-label" htmlFor="pq-name">Full name</label>
                    <input id="pq-name" data-testid="pq-input-name" required className="input-field" value={form.full_name} onChange={set("full_name")} placeholder="Thandi Mokoena" />
                  </div>
                  <div>
                    <label className="input-label" htmlFor="pq-email">Email</label>
                    <input id="pq-email" data-testid="pq-input-email" type="email" required className="input-field" value={form.email} onChange={set("email")} placeholder="you@example.co.za" />
                  </div>
                  <div>
                    <label className="input-label" htmlFor="pq-mobile">Mobile</label>
                    <input id="pq-mobile" data-testid="pq-input-mobile" required className="input-field" value={form.mobile} onChange={set("mobile")} placeholder="082 000 0000" />
                  </div>
                  <div>
                    <label className="input-label" htmlFor="pq-employment">Employment</label>
                    <select id="pq-employment" data-testid="pq-select-employment" className="input-field" value={form.employment_type} onChange={set("employment_type")}>
                      <option value="employed">Employed</option>
                      <option value="self-employed">Self-employed</option>
                    </select>
                  </div>
                  <div>
                    <label className="input-label" htmlFor="pq-term">Preferred term</label>
                    <select id="pq-term" data-testid="pq-select-term" className="input-field" value={form.preferred_term} onChange={set("preferred_term")}>
                      <option value={48}>48 months</option>
                      <option value={60}>60 months</option>
                    </select>
                  </div>
                  <div>
                    <label className="input-label" htmlFor="pq-income">Net monthly income (R)</label>
                    <input id="pq-income" data-testid="pq-input-income" type="number" min="0" required className="input-field" value={form.net_income} onChange={set("net_income")} placeholder="18000" />
                  </div>
                  <div>
                    <label className="input-label" htmlFor="pq-expenses">Monthly expenses (R)</label>
                    <input id="pq-expenses" data-testid="pq-input-expenses" type="number" min="0" required className="input-field" value={form.monthly_expenses} onChange={set("monthly_expenses")} placeholder="11000" />
                  </div>
                  <div>
                    <label className="input-label" htmlFor="pq-maxpay">Max affordable car payment (R)</label>
                    <input id="pq-maxpay" data-testid="pq-input-maxpay" type="number" min="0" required className="input-field" value={form.max_affordable_payment} onChange={set("max_affordable_payment")} placeholder="5500" />
                  </div>
                  <div>
                    <label className="input-label" htmlFor="pq-deposit">Available deposit (R)</label>
                    <input id="pq-deposit" data-testid="pq-input-deposit" type="number" min="0" className="input-field" value={form.ownership_deposit} onChange={set("ownership_deposit")} placeholder="10000" />
                  </div>
                  <div>
                    <label className="input-label" htmlFor="pq-licence">Valid driver's licence?</label>
                    <select id="pq-licence" data-testid="pq-select-licence" className="input-field" value={form.has_valid_licence} onChange={set("has_valid_licence")}>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  <div>
                    <label className="input-label" htmlFor="pq-use">Private use only? (no e-hailing)</label>
                    <select id="pq-use" data-testid="pq-select-use" className="input-field" value={form.private_use_only} onChange={set("private_use_only")}>
                      <option value="yes">Yes, private use</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="input-label" htmlFor="pq-vehicle">Vehicle you're eyeing (optional)</label>
                    <input id="pq-vehicle" data-testid="pq-input-vehicle" className="input-field" value={form.vehicle_interest} onChange={set("vehicle_interest")} placeholder="e.g. Toyota Corolla Quest" />
                  </div>
                </div>
                {referral && (
                  <p data-testid="referral-badge" className="flex items-center gap-2 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md px-4 py-3">
                    <Gift size={16} className="shrink-0" /> Referred by {referral.name} — code {referral.code} applied
                  </p>
                )}
                <button
                  type="submit"
                  data-testid="pq-submit-btn"
                  disabled={loading}
                  className="w-full bg-[#F57C00] hover:bg-[#E67300] disabled:opacity-60 text-white font-bold px-6 py-4 rounded-md transition-colors duration-300 flex items-center justify-center gap-2"
                >
                  {loading && <Loader2 size={18} className="animate-spin" />}
                  {loading ? "Checking…" : "Check my eligibility"}
                </button>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  This is a provisional indication only — not an approval,
                  quotation or agreement. Your information is processed under
                  POPIA for assessment purposes.
                </p>
              </motion.form>
            ) : (
              <motion.div
                key="result"
                data-testid={style.testid}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className={`bg-[#122659]/80 backdrop-blur-xl border ${style.ring} rounded-xl p-8 md:p-10`}
              >
                <style.icon size={48} className={`${style.color} mb-6`} />
                <h2 className="font-display text-3xl font-black text-white mb-3" data-testid="result-label">{result.label}</h2>
                <p className="text-sm text-slate-300 leading-relaxed mb-8" data-testid="result-message">{result.message}</p>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="bg-[#0B1F4E] rounded-lg p-4 border border-sky-300/20">
                    <p className="text-[10px] uppercase tracking-widest text-sky-300 font-bold mb-1">Est. surplus</p>
                    <p className="font-display text-xl font-black text-white" data-testid="result-surplus">{zar(result.surplus)}</p>
                  </div>
                  <div className="bg-[#0B1F4E] rounded-lg p-4 border border-sky-300/20">
                    <p className="text-[10px] uppercase tracking-widest text-sky-300 font-bold mb-1">Suggested max / month</p>
                    <p className="font-display text-xl font-black text-white" data-testid="result-recommended">{zar(result.recommended_max)}</p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    data-testid="result-continue-btn"
                    onClick={() => navigate("/apply")}
                    className="flex-1 bg-[#F57C00] hover:bg-[#E67300] text-white font-bold px-6 py-4 rounded-md transition-colors duration-300"
                  >
                    Continue to full application
                  </button>
                  <button
                    data-testid="result-restart-btn"
                    onClick={() => setResult(null)}
                    className="border-2 border-[#87CEEB] text-[#87CEEB] hover:bg-[#87CEEB] hover:text-[#0B1F4E] font-bold px-6 py-4 rounded-md transition-colors duration-300"
                  >
                    Start over
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 mt-6">
                  A copy of this result has been emailed to you. Questions?{" "}
                  <Link to="/contact" className="text-sky-300 hover:text-white">Contact us</Link> or call 010 534 9926.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </Reveal>
      </div>
    </div>
  );
}
